import { DateTime } from 'luxon';
import i18n from '../i18n';

const validateExp = new RegExp(
  '([0-9]{1,2}:)?([0-5])?[0-9]:[0-5][0-9]([.][0-9]{1,2})?'
);

function parseTime(secs, addZero = true) {
  let hours = Math.floor(secs / 3600);
  let minutes = Math.floor((secs - hours * 3600) / 60);
  let seconds = Math.floor(secs - hours * 3600 - minutes * 60);
  let milliSeconds = Math.round(
    (secs - hours * 3600 - minutes * 60 - seconds) * 1000
  );

  if (addZero && hours < 10) {
    hours = `0${hours}`;
  }

  if (addZero && minutes < 10) {
    minutes = `0${minutes}`;
  }

  if (addZero && seconds < 10) {
    seconds = `0${seconds}`;
  }

  if (addZero && milliSeconds < 10) {
    milliSeconds = `00${milliSeconds}`;
  } else if (addZero && milliSeconds < 100) {
    milliSeconds = `0${milliSeconds}`;
  }

  return {
    hours: hours.toString(),
    minutes: minutes.toString(),
    seconds: seconds.toString(),
    milliSeconds: milliSeconds.toString(),
  };
}

export function parseTimeStr(timeStr) {
  if (typeof timeStr !== 'string') return null;
  const split = timeStr.split(':');
  let hours = 0;
  let minutes = 0;
  let seconds = 0;
  if (split.length === 2) {
    [minutes, seconds] = split;
  } else if (split.length === 3) {
    [hours, minutes, seconds] = split;
  }
  return (
    parseInt(hours, 10) * 3600 + parseInt(minutes, 10) * 60 + parseInt(seconds)
  );
}

export function formatTimeSrt(secs) {
  // Difference between vtt and srt is vtt uses . instead of , between seconds and milliseconds.
  const { hours, minutes, seconds, milliSeconds } = parseTime(secs);
  return `${hours}:${minutes}:${seconds},${milliSeconds}`;
}

export function formatTimeVtt(secs) {
  // Difference between vtt and srt is vtt uses . instead of , between seconds and milliseconds.
  const { hours, minutes, seconds, milliSeconds } = parseTime(secs);
  return `${hours}:${minutes}:${seconds}.${milliSeconds}`;
}

export function formatShortTimeFormat(seconds) {
  const secs = Math.floor(seconds);
  const minutes = Math.floor(secs / 60);
  const hours = Math.floor(minutes / 60);
  const formattedSeconds = secs % 60;
  const formattedMinutes = minutes % 60;
  const formattedHours = hours % 60;

  const formattedSecondsString = formattedSeconds.toString().padStart(2, '0');

  if (hours === 0) {
    return `${formattedMinutes}:${formattedSecondsString}`;
  }

  const formattedMinutesString = formattedMinutes.toString().padStart(2, '0');

  return `${formattedHours}:${formattedMinutesString}:${formattedSecondsString}`;
}

export function formatTime(secs, format = 'HH:mm:ss.SSS') {
  let { hours, minutes, seconds, milliSeconds } = parseTime(secs);

  // Return formatted time
  if (format === 'HH:mm:ss.SSS') {
    return `${hours}:${minutes}:${seconds}.${milliSeconds}`;
  } else if (format === 'HH:mm:ss.SS') {
    return `${hours}:${minutes}:${seconds}.${milliSeconds.slice(0, 2)}`;
  } else if (format === 'HH:mm:ss') {
    return `${hours}:${minutes}:${seconds}`;
  } else if (format === 'HH:mm') {
    return `${hours}:${minutes}`;
  } else if (format === 'mm:ss') {
    // TODO
    return `${minutes}:${seconds}`;
  } else if (format === 'short') {
    return formatShortTimeFormat(secs);
  }
}

export function strToSecs(timeStr) {
  const parts = timeStr.split(':');

  let total = 0;
  let multiplier = Math.max(60 ** (parts.length - 1), 0);
  for (const p of parts) {
    total += parseFloat(p) * multiplier;
    multiplier /= 60;
  }

  return `${total.toFixed(2)}`;
}

export function validateTime(timeStr) {
  if (!timeStr) return false;
  const match = timeStr.match(validateExp)?.[0];
  return match && match.length === timeStr.length;
}

export function formatElapsedTime(isoString) {
  const ts = DateTime.fromISO(isoString);
  const now = DateTime.now();

  //diff in seconds
  const diff = Math.floor(now.diff(ts).milliseconds / 1000);

  if (diff < 60 * 60) {
    //less than hour
    return `${Math.floor(diff / 60)} ${i18n.t('min')}`;
  }

  if (diff < 60 * 60 * 24) {
    //less than one day
    return `${Math.floor(diff / (60 * 60))} ${i18n.t('timmar')}`;
  }

  if (diff < 60 * 60 * 24 * 30) {
    return `${Math.floor(diff / (60 * 60 * 24))} ${i18n.t('dagar')}`;
  }

  return `30+ ${i18n.t('dagar')}`;
}

export function validateSegments(idx, segments) {
  if (!segments[idx]) return { val: true };

  const start = parseFloat(strToSecs(segments[idx].formattedStart));
  const end = parseFloat(strToSecs(segments[idx].formattedEnd));
  if (end <= start) {
    return { val: false, reason: 'current' };
  }

  if (idx > 0) {
    const prev = segments[idx - 1];
    const prevEnd = parseFloat(strToSecs(prev.formattedEnd));
    if (start < prevEnd || end < prevEnd) {
      return { val: false, reason: 'prev' };
    }
  }

  if (segments[idx + 1]) {
    const next = segments[idx + 1];
    const nextStart = parseFloat(strToSecs(next.formattedStart));
    const nextEnd = parseFloat(strToSecs(next.formattedEnd));
    if (end > nextStart || end > nextEnd) {
      return { val: false, reason: 'next' };
    }
  }
  return { val: true, reason: 'both' };
}
